import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react"
import {
  AlertCircle,
  Check,
  ChevronLeft,
  ChevronRight,
  Info,
  Loader2,
  Sparkles,
  X,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import type { ReasoningStep } from "@f2e/shared/types"
import { ReasoningStepStatus, ReasoningStepType } from "@f2e/shared/types"
import { CopyButton, TipIconButton } from "@f2e/ui/components/Button"
import {
  getReasoningStepTypeLabels,
  MessageBlock,
  ToolPartCard,
} from "@f2e/ui/components/Chat"
import {
  Badge,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import type { AutosizeTextAreaRef } from "@f2e/ui/components/base-plus"
import { useUiTranslation } from "@f2e/ui/i18n"
import { cn } from "@f2e/ui/lib/utils"

import {
  useApplyAIDraftApi,
  aiDraftTargetKey,
  useDismissAIDraftApi,
  useGenerateAIDraftApi,
  useGetAIDraftApi,
  type AIDraftDismissReason,
  type AIDraftFormPrefillValue,
  type AIDraftFormProposal,
  type AIDraftResponse,
  type AIDraftSummaryResponse,
  type AIDraftToolActivity,
  type AIDraftTarget,
  type GenerateAIDraftRequest,
} from "@/hooks/api"
import { UsageMetaTooltip } from "@/components/Conversation/UsageMetaTooltip"
import { normalizeApiError } from "@/lib/apiErrors"
import type {
  AIDraftToolSupportStatus,
  AIDraftUnsupportedTool,
  UnifiedToolType,
} from "@/types"

import { AIDraftFormProposalPreview } from "./AIDraftFormProposalPreview"

const AI_DRAFT_REQUEST_TIMEOUT_MS = 615_000
const AI_DRAFT_GUIDANCE_MAX_LENGTH = 2_000
const AI_DRAFT_REFINEMENT_STALE_ERROR_CODE = "draft_refinement_stale"
const AI_DRAFT_TOOL_TYPE_KEYS: Record<UnifiedToolType, string> = {
  knowledge_base: "knowledgeBase",
  web_search: "webSearch",
  call_agent: "callAgent",
  image_generation: "imageGeneration",
  request_form: "requestForm",
  payment: "payment",
  memory: "memory",
  http_request: "httpRequest",
  text2speech: "textToSpeech",
  tabular_data: "tabularData",
}
const codePointLength = (value: string) => Array.from(value).length
const getAIDraftRejectionCode = (error: unknown, fallback: string) => {
  const data = normalizeApiError(error).data
  if (data && typeof data === "object" && "code" in data) {
    const code = (data as { code?: unknown }).code
    if (typeof code === "string") return code
  }
  return fallback
}
type AIDraftApplyMode = "replace" | "insert"

export interface AIDraftContextAnchor {
  part_id: number
  sequence: number
}

export interface AppliedDraftComposerForm {
  proposal: AIDraftFormProposal
  values: Record<string, AIDraftFormPrefillValue>
}

export interface AppliedDraftComposerState {
  id: number
  context_through_part_id: number
  context_through_sequence: number
  /** Null when the draft proposed no form or the operator removed it. */
  form: AppliedDraftComposerForm | null
}

export const getAIDraftFormProposal = (
  draft: Pick<AIDraftResponse, "proposed_actions">
): AIDraftFormProposal | null => draft.proposed_actions?.request_form ?? null

export const getAppliedDraftComposerForm = (
  draft: Pick<AIDraftResponse, "proposed_actions">
): AppliedDraftComposerForm | null => {
  const proposal = getAIDraftFormProposal(draft)
  if (!proposal) return null

  return {
    proposal,
    values: { ...proposal.effective_prefilled_values },
  }
}

export interface AIDraftContextWarningProps {
  message: string
  disabled: boolean
  onRegenerate: () => void
  className?: string
}

export const AIDraftContextWarning = ({
  message,
  disabled,
  onRegenerate,
  className,
}: AIDraftContextWarningProps) => {
  const { t } = useTranslation()

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 border border-amber-300/70 bg-amber-50 px-3 py-2.5 text-sm text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100",
        className
      )}
      role="status"
      aria-live="polite"
      data-testid="ai-draft-context-warning"
    >
      <div className="flex min-w-0 flex-1 items-start gap-2">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{message}</span>
      </div>
      <Button size="sm" disabled={disabled} onClick={onRegenerate}>
        {t("history.aiDraft.generateFromLatest")}
      </Button>
    </div>
  )
}

const isDraftContextStale = (
  draft: Pick<
    AIDraftResponse,
    "context_through_part_id" | "context_through_sequence"
  >,
  currentContextAnchor: AIDraftContextAnchor | null
) =>
  currentContextAnchor != null &&
  (currentContextAnchor.part_id > draft.context_through_part_id ||
    (currentContextAnchor.part_id === draft.context_through_part_id &&
      currentContextAnchor.sequence !== draft.context_through_sequence))

const activityToReasoningStep = (
  activity: AIDraftToolActivity
): ReasoningStep => {
  const failed = activity.status === "recoverable_failed"
  const tabularResult =
    activity.tool_name === "tabular_query"
      ? failed
        ? tabularActivityErrorResult(activity)
        : tabularActivityResult(activity)
      : undefined
  const httpResult =
    activity.tool_name === "http_request" ? activity.display_result : undefined
  return {
    id: activity.tool_call_id,
    type: `consultant_${activity.tool_name}` as ReasoningStep["type"],
    content: activity.tool_name,
    status: failed ? ReasoningStepStatus.Failed : ReasoningStepStatus.Completed,
    args: activity.display_args,
    result:
      tabularResult ??
      httpResult ??
      (failed ? undefined : activity.display_result),
    error: failed
      ? String(activity.display_result.error_code ?? "tool_unavailable")
      : undefined,
    duration: activity.duration_ms,
  }
}

const tabularActivityOperation = (
  activity: AIDraftToolActivity
): "inspect" | "query" =>
  activity.display_args.operation === "query" ? "query" : "inspect"

const tabularActivityErrorResult = (activity: AIDraftToolActivity) => {
  const errorCode =
    typeof activity.display_result.error_code === "string"
      ? activity.display_result.error_code
      : "tool_unavailable"
  return {
    status: "error" as const,
    operation: tabularActivityOperation(activity),
    error: {
      code: errorCode,
      message: errorCode,
    },
  }
}

const tabularActivityResult = (activity: AIDraftToolActivity) => {
  const args = activity.display_args
  const result = activity.display_result
  const operation = tabularActivityOperation(activity)
  const queryType =
    operation === "query" &&
    (args.query_type === "rows" || args.query_type === "aggregate")
      ? args.query_type
      : undefined
  const freshnessStatus =
    result.freshness_status === "stale" ? "stale" : "current"
  const warningCodes = Array.isArray(result.warning_codes)
    ? result.warning_codes.filter(
        (value): value is string => typeof value === "string"
      )
    : []

  return {
    status: "success" as const,
    operation,
    data: {
      operation,
      query_type: queryType,
      source: {
        name:
          typeof result.source_name === "string"
            ? result.source_name
            : undefined,
      },
      matched_count:
        typeof result.matched_count === "number"
          ? result.matched_count
          : undefined,
      returned_count:
        typeof result.returned_count === "number"
          ? result.returned_count
          : undefined,
      freshness: { status: freshnessStatus },
      warnings: warningCodes.map((code) => ({ code, message: code })),
      truncated: result.truncated === true,
    },
  }
}

export const AIDraftActivityCards = ({
  activities,
}: {
  activities: AIDraftToolActivity[]
}) => {
  const { t: uiT } = useUiTranslation()
  const stepTypeLabels = useMemo(() => getReasoningStepTypeLabels(uiT), [uiT])
  const steps = useMemo(
    () =>
      [...activities]
        .sort((a, b) => a.sequence - b.sequence)
        .map(activityToReasoningStep),
    [activities]
  )
  if (steps.length === 0) return null
  return (
    <div className="flex flex-col gap-4 [&>*]:mb-0">
      {steps.map((step) => (
        <ToolPartCard
          key={step.id}
          step={step}
          label={
            stepTypeLabels[step.type as keyof typeof stepTypeLabels] ??
            step.content
          }
          hideSensitiveReasoningDetails={true}
          showSafeTabularSummary={
            step.type === ReasoningStepType.ConsultantTabularQuery
          }
          showSafeKnowledgeBaseSummary={
            step.type === ReasoningStepType.ConsultantRetrieveContextObjs
          }
          showSafeHttpRequestSummary={
            step.type === ReasoningStepType.ConsultantHttpRequest
          }
        />
      ))}
    </div>
  )
}

const AIDraftMarkdown = ({
  content,
  resultType,
  actionBar = <></>,
  className,
}: {
  content: string
  resultType?: AIDraftResponse["result_type"]
  actionBar?: ReactNode
  className?: string
}) => {
  const { t } = useTranslation()
  if (resultType === "human_handoff") {
    return (
      <div className={className}>
        <div className="flex items-start gap-2 text-sm" role="status">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-medium">{t("history.aiDraft.handoffTitle")}</p>
            <p className="mt-1">{t("history.aiDraft.handoffDescription")}</p>
          </div>
        </div>
        {actionBar}
      </div>
    )
  }
  return (
    <div className={cn("[&>.content]:mt-0", className)}>
      <MessageBlock
        message={{ role: "assistant", content }}
        customActionBar={actionBar}
        hideSensitiveReasoningDetails={true}
      />
    </div>
  )
}

const dismissReasons: AIDraftDismissReason[] = [
  "incorrect",
  "tone_mismatch",
  "incomplete",
  "outdated",
  "other",
]

interface AIDraftComposerProps {
  draftTarget: AIDraftTarget
  message: string
  currentContextAnchor: AIDraftContextAnchor | null
  appliedDraftContext: AppliedDraftComposerState | null
  disabled?: boolean
  unsupportedTools?: AIDraftUnsupportedTool[]
  toolSupportStatus?: AIDraftToolSupportStatus
  textareaRef: RefObject<AutosizeTextAreaRef | null>
  onMessageChange: (value: string) => void
  onAppliedDraftChange: (draft: AppliedDraftComposerState | null) => void
  onComposerPendingChange: (targetKey: string, pending: boolean) => void
  onRecordsChanged: () => void
  onBillingChanged: () => void
  onPermissionDenied: () => void
}

export const AIDraftComposer = ({
  draftTarget,
  message,
  currentContextAnchor,
  appliedDraftContext,
  disabled = false,
  unsupportedTools = [],
  toolSupportStatus = "unavailable",
  textareaRef,
  onMessageChange,
  onAppliedDraftChange,
  onComposerPendingChange,
  onRecordsChanged,
  onBillingChanged,
  onPermissionDenied,
}: AIDraftComposerProps) => {
  const { t } = useTranslation()
  const targetKey = aiDraftTargetKey(draftTarget)
  const { fetchFn: generateDraft } = useGenerateAIDraftApi(draftTarget)
  const [draft, setDraft] = useState<AIDraftResponse | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [pendingOutcome, setPendingOutcome] = useState<
    "apply" | "dismiss" | null
  >(null)
  const [showConflict, setShowConflict] = useState(false)
  const [showDismissReasons, setShowDismissReasons] = useState(false)
  const [showRefinement, setShowRefinement] = useState(false)
  const [generationInstruction, setGenerationInstruction] = useState("")
  const [selectedDismissReason, setSelectedDismissReason] =
    useState<AIDraftDismissReason | null>(null)
  const [dismissFeedback, setDismissFeedback] = useState("")
  const [retryGenerateRequest, setRetryGenerateRequest] =
    useState<GenerateAIDraftRequest | null>(null)
  const [retryApplyMode, setRetryApplyMode] = useState<AIDraftApplyMode | null>(
    null
  )
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [dismissErrorCode, setDismissErrorCode] = useState<string | null>(null)
  const [permissionDenied, setPermissionDenied] = useState(false)
  const [isStale, setIsStale] = useState(false)
  const requestIdRef = useRef(0)
  const outcomeRequestIdRef = useRef(0)
  const abortRef = useRef<AbortController | null>(null)
  const isOutcomePending = pendingOutcome !== null
  const actionsDisabled = disabled || permissionDenied
  const generationInstructionLength = codePointLength(generationInstruction)
  const dismissFeedbackLength = codePointLength(dismissFeedback)
  const unsupportedToolNames = useMemo(
    () =>
      unsupportedTools.map(
        (tool) =>
          tool.name?.trim() ||
          t(
            `history.aiDraft.unsupportedTools.toolTypes.${AI_DRAFT_TOOL_TYPE_KEYS[tool.type]}`
          )
      ),
    [t, unsupportedTools]
  )
  const isToolSupportUnavailable = toolSupportStatus === "unavailable"
  const showToolSupportInfo =
    isToolSupportUnavailable || unsupportedToolNames.length > 0
  const isAppliedDraftStale =
    appliedDraftContext != null &&
    isDraftContextStale(appliedDraftContext, currentContextAnchor)

  const resetDismissForm = useCallback(() => {
    setShowDismissReasons(false)
    setSelectedDismissReason(null)
    setDismissFeedback("")
    setDismissErrorCode(null)
  }, [])

  const handlePermissionDenied = useCallback(
    (error: unknown) => {
      if (normalizeApiError(error).kind !== "permission_denied") return false

      setPermissionDenied(true)
      setErrorCode(null)
      setDismissErrorCode(null)
      setRetryGenerateRequest(null)
      setRetryApplyMode(null)
      setShowConflict(false)
      setShowRefinement(false)
      setGenerationInstruction("")
      resetDismissForm()
      onPermissionDenied()
      return true
    },
    [onPermissionDenied, resetDismissForm]
  )

  const { fetchFn: applyDraft } = useApplyAIDraftApi(
    draftTarget,
    draft?.id ?? -1
  )
  const { fetchFn: dismissDraft } = useDismissAIDraftApi(
    draftTarget,
    draft?.id ?? -1
  )

  useEffect(() => {
    requestIdRef.current += 1
    outcomeRequestIdRef.current += 1
    abortRef.current?.abort()
    abortRef.current = null
    setDraft(null)
    setErrorCode(null)
    setPermissionDenied(false)
    setIsGenerating(false)
    setPendingOutcome(null)
    setShowConflict(false)
    setShowRefinement(false)
    setGenerationInstruction("")
    resetDismissForm()
    setRetryGenerateRequest(null)
    setRetryApplyMode(null)
    setIsStale(false)
    return () => {
      requestIdRef.current += 1
      outcomeRequestIdRef.current += 1
      abortRef.current?.abort()
      onComposerPendingChange(targetKey, false)
    }
  }, [onComposerPendingChange, resetDismissForm, targetKey])

  useEffect(() => {
    if (draft && isDraftContextStale(draft, currentContextAnchor)) {
      setIsStale(true)
    }
  }, [currentContextAnchor, draft])

  const executeGeneration = useCallback(
    async (
      requestBody: GenerateAIDraftRequest = {},
      moveCurrentDraftToRecords = true
    ) => {
      const instruction = requestBody.instruction?.trim() || undefined
      const normalizedRequest: GenerateAIDraftRequest = {
        ...(instruction ? { instruction } : {}),
        ...(requestBody.refinement_source_draft_id
          ? {
              refinement_source_draft_id:
                requestBody.refinement_source_draft_id,
            }
          : {}),
      }
      const requestId = requestIdRef.current + 1
      requestIdRef.current = requestId
      const controller = new AbortController()
      abortRef.current = controller
      setRetryGenerateRequest(normalizedRequest)
      setRetryApplyMode(null)
      setIsGenerating(true)
      onComposerPendingChange(targetKey, true)
      if (draft && moveCurrentDraftToRecords) onRecordsChanged()
      if (!normalizedRequest.refinement_source_draft_id) setDraft(null)
      setIsStale(false)
      setErrorCode(null)
      setDismissErrorCode(null)
      setShowConflict(false)
      setShowDismissReasons(false)
      try {
        let activeRequest = normalizedRequest
        while (true) {
          try {
            const response = await generateDraft({
              ...(Object.keys(activeRequest).length
                ? { jsonBody: activeRequest }
                : {}),
              retry: 0,
              timeout: AI_DRAFT_REQUEST_TIMEOUT_MS,
              signal: controller.signal,
            })
            if (requestId !== requestIdRef.current || controller.signal.aborted)
              return
            if (!response?.data) throw new Error("draft_generation_failed")
            setDraft(response.data)
            setIsStale(isDraftContextStale(response.data, currentContextAnchor))
            setRetryGenerateRequest(null)
            setShowRefinement(false)
            setGenerationInstruction("")
            onBillingChanged()
            return
          } catch (error) {
            if (controller.signal.aborted || requestId !== requestIdRef.current)
              return
            if (handlePermissionDenied(error)) return
            const errorCode = getAIDraftRejectionCode(
              error,
              "draft_generation_failed"
            )
            if (
              errorCode === AI_DRAFT_REFINEMENT_STALE_ERROR_CODE &&
              activeRequest.refinement_source_draft_id
            ) {
              activeRequest = {
                ...(activeRequest.instruction
                  ? { instruction: activeRequest.instruction }
                  : {}),
              }
              setRetryGenerateRequest(activeRequest)
              setDraft(null)
              setShowRefinement(false)
              continue
            }
            setErrorCode(errorCode)
            onBillingChanged()
            return
          }
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setIsGenerating(false)
          onComposerPendingChange(targetKey, false)
        }
      }
    },
    [
      draft,
      generateDraft,
      handlePermissionDenied,
      currentContextAnchor,
      onBillingChanged,
      onComposerPendingChange,
      onRecordsChanged,
      targetKey,
    ]
  )

  const handleGenerate = useCallback(
    (requestBody: GenerateAIDraftRequest = {}) => {
      if (actionsDisabled || isGenerating || isOutcomePending) return
      void executeGeneration(requestBody)
    },
    [actionsDisabled, executeGeneration, isGenerating, isOutcomePending]
  )

  const focusComposer = useCallback(
    (outcomeRequestId: number) => {
      window.requestAnimationFrame(() => {
        if (outcomeRequestId !== outcomeRequestIdRef.current) return
        const textarea = textareaRef.current?.textArea
        if (!textarea) return
        textarea.focus()
        const end = textarea.value.length
        textarea.setSelectionRange(end, end)
      })
    },
    [textareaRef]
  )

  const applyWithMode = useCallback(
    async (mode: AIDraftApplyMode) => {
      if (
        actionsDisabled ||
        !draft ||
        isOutcomePending ||
        draft.result_type === "human_handoff"
      )
        return
      const outcomeRequestId = outcomeRequestIdRef.current + 1
      outcomeRequestIdRef.current = outcomeRequestId
      const targetDraft = draft
      const baseMessage = message
      setPendingOutcome("apply")
      onComposerPendingChange(targetKey, true)
      setErrorCode(null)
      setRetryApplyMode(null)
      try {
        const response = await applyDraft({ retry: 0 })
        if (outcomeRequestId !== outcomeRequestIdRef.current) return
        if (!response?.data) throw new Error("draft_apply_failed")
        const nextMessage =
          mode === "insert" && baseMessage.trim()
            ? `${baseMessage.trimEnd()}\n\n${targetDraft.content}`
            : targetDraft.content
        onMessageChange(nextMessage)
        onAppliedDraftChange({
          id: targetDraft.id,
          context_through_part_id: targetDraft.context_through_part_id,
          context_through_sequence: targetDraft.context_through_sequence,
          form: getAppliedDraftComposerForm(targetDraft),
        })
        setDraft(null)
        setShowConflict(false)
        resetDismissForm()
        onRecordsChanged()
        focusComposer(outcomeRequestId)
      } catch (error) {
        if (outcomeRequestId !== outcomeRequestIdRef.current) return
        if (handlePermissionDenied(error)) return
        setErrorCode(getAIDraftRejectionCode(error, "draft_apply_failed"))
        setRetryApplyMode(mode)
      } finally {
        if (outcomeRequestId === outcomeRequestIdRef.current) {
          setPendingOutcome(null)
          onComposerPendingChange(targetKey, false)
        }
      }
    },
    [
      applyDraft,
      actionsDisabled,
      draft,
      focusComposer,
      handlePermissionDenied,
      isOutcomePending,
      message,
      onAppliedDraftChange,
      onComposerPendingChange,
      onMessageChange,
      onRecordsChanged,
      resetDismissForm,
      targetKey,
    ]
  )

  const handleApply = () => {
    if (actionsDisabled || isOutcomePending) return
    if (message.trim()) {
      setShowConflict(true)
      return
    }
    void applyWithMode("replace")
  }

  const handleDismiss = useCallback(
    async ({
      reason,
      feedback,
      regenerate,
    }: {
      reason?: AIDraftDismissReason
      feedback?: string
      regenerate?: boolean
    }) => {
      if (actionsDisabled || !draft || isOutcomePending) return
      const normalizedFeedback = feedback?.trim() || undefined
      if (regenerate && reason === "other" && !normalizedFeedback) return
      const outcomeRequestId = outcomeRequestIdRef.current + 1
      outcomeRequestIdRef.current = outcomeRequestId
      const sourceDraftId = draft.id
      const hasReply = draft.result_type !== "human_handoff"
      setPendingOutcome("dismiss")
      setErrorCode(null)
      setDismissErrorCode(null)
      if (regenerate) onComposerPendingChange(targetKey, true)
      let generationStarted = false
      try {
        const response = await dismissDraft({
          jsonBody: {
            ...(reason ? { reason } : {}),
            ...(normalizedFeedback ? { feedback: normalizedFeedback } : {}),
          },
          retry: 0,
        })
        if (outcomeRequestId !== outcomeRequestIdRef.current) return
        if (!response?.data) throw new Error("draft_dismiss_failed")
        setDraft(response.data)
        resetDismissForm()
        onRecordsChanged()
        if (regenerate) {
          generationStarted = true
          setPendingOutcome(null)
          await executeGeneration(
            {
              ...(hasReply
                ? { refinement_source_draft_id: sourceDraftId }
                : {}),
            },
            false
          )
        } else {
          setDraft(null)
        }
      } catch (error) {
        if (outcomeRequestId !== outcomeRequestIdRef.current) return
        if (handlePermissionDenied(error)) return
        setDismissErrorCode(
          getAIDraftRejectionCode(error, "draft_dismiss_failed")
        )
      } finally {
        if (outcomeRequestId === outcomeRequestIdRef.current) {
          setPendingOutcome(null)
          if (regenerate && !generationStarted)
            onComposerPendingChange(targetKey, false)
        }
      }
    },
    [
      actionsDisabled,
      dismissDraft,
      draft,
      executeGeneration,
      handlePermissionDenied,
      isOutcomePending,
      onComposerPendingChange,
      onRecordsChanged,
      resetDismissForm,
      targetKey,
    ]
  )

  return (
    <div className="mb-3 space-y-2" data-testid="ai-draft-composer">
      {isAppliedDraftStale && !isGenerating && !draft && (
        <AIDraftContextWarning
          message={t("history.aiDraft.appliedContextStale")}
          disabled={actionsDisabled || isGenerating || isOutcomePending}
          onRegenerate={() => handleGenerate()}
          className="rounded-lg"
        />
      )}

      {isGenerating && (
        <div
          className="flex items-start gap-3 rounded-lg border border-border/80 bg-muted/30 px-3 py-3 text-sm"
          role="status"
          aria-live="polite"
        >
          <Loader2
            className="mt-0.5 h-4 w-4 shrink-0 animate-spin"
            aria-hidden="true"
          />
          <div>
            <div className="font-medium">{t("history.aiDraft.generating")}</div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {t("history.aiDraft.generatingDescription")}
            </div>
          </div>
        </div>
      )}

      {(permissionDenied || errorCode) && !isGenerating && (
        <div
          className="flex items-start justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive"
          role="alert"
        >
          <div className="flex min-w-0 items-start gap-2">
            <AlertCircle
              className="mt-0.5 h-4 w-4 shrink-0"
              aria-hidden="true"
            />
            <div>
              <div>
                {permissionDenied
                  ? t("common.noPermission")
                  : t(`history.aiDraft.errors.${errorCode}`, {
                      defaultValue: t("history.aiDraft.errors.generic"),
                    })}
              </div>
              {!permissionDenied && retryGenerateRequest && (
                <div className="mt-1 text-xs text-muted-foreground">
                  {t("history.aiDraft.retryNotice")}
                </div>
              )}
            </div>
          </div>
          {!permissionDenied && !isAppliedDraftStale && (
            <Button
              size="sm"
              variant="outline"
              disabled={actionsDisabled}
              onClick={() => {
                if (retryApplyMode) {
                  void applyWithMode(retryApplyMode)
                  return
                }
                handleGenerate(
                  errorCode === AI_DRAFT_REFINEMENT_STALE_ERROR_CODE
                    ? {
                        ...(retryGenerateRequest?.instruction
                          ? { instruction: retryGenerateRequest.instruction }
                          : {}),
                      }
                    : (retryGenerateRequest ?? {})
                )
              }}
            >
              {errorCode === AI_DRAFT_REFINEMENT_STALE_ERROR_CODE
                ? t("history.aiDraft.generateFromLatest")
                : t("history.aiDraft.retry")}
            </Button>
          )}
        </div>
      )}

      {draft && !isGenerating && !showConflict && (
        <section
          className="overflow-hidden rounded-lg border border-border/80 bg-background"
          aria-label={t("history.aiDraft.previewLabel")}
        >
          <header className="flex items-center justify-between border-b border-border/70 px-3 py-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              {t(
                draft.mode === "agent_preview"
                  ? "history.aiDraft.agentPreviewTitle"
                  : "history.aiDraft.title"
              )}
            </div>
            <Badge variant="outline">
              {draft.outcome === "generated"
                ? t(
                    draft.result_type === "human_handoff"
                      ? "history.aiDraft.previewCompleted"
                      : "history.aiDraft.notApplied"
                  )
                : t(`history.aiDraft.outcomes.${draft.outcome}`)}
            </Badge>
          </header>
          {isStale && draft.outcome === "generated" && (
            <AIDraftContextWarning
              message={t("history.aiDraft.stale")}
              disabled={actionsDisabled || isGenerating || isOutcomePending}
              onRegenerate={() => handleGenerate()}
              className="border-x-0 border-t-0"
            />
          )}
          <div className="max-h-[min(40vh,360px)] overflow-y-auto px-3 py-3">
            <AIDraftActivityCards activities={draft.tool_activities} />
            <AIDraftMarkdown
              content={draft.content}
              resultType={draft.result_type}
              className={draft.tool_activities.length ? "mt-6" : undefined}
            />
            <AIDraftFormProposalPreview
              proposal={getAIDraftFormProposal(draft)}
            />
          </div>
          {draft.outcome === "generated" && (
            <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-border/70 bg-muted/20 px-3 py-2">
              <div className="flex flex-wrap gap-2">
                {draft.result_type !== "human_handoff" && (
                  <Button
                    size="sm"
                    variant={isStale ? "outline" : "default"}
                    disabled={actionsDisabled || isOutcomePending}
                    onClick={handleApply}
                  >
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                    {t("history.aiDraft.apply")}
                  </Button>
                )}
                {!isStale && (
                  <Popover
                    open={showRefinement}
                    onOpenChange={(open) => {
                      setShowRefinement(open)
                      if (open) {
                        resetDismissForm()
                      } else {
                        setGenerationInstruction("")
                      }
                    }}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={actionsDisabled || isOutcomePending}
                      >
                        {t("history.aiDraft.generateAgain")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      sideOffset={8}
                      className="w-[min(22rem,calc(100vw-2rem))] space-y-3 p-3"
                      aria-label={t("history.aiDraft.generateAgain")}
                    >
                      <div className="space-y-1.5">
                        <label
                          className="block text-sm font-medium"
                          htmlFor={`ai-draft-instruction-${draft.id}`}
                        >
                          {t(
                            draft.result_type === "human_handoff"
                              ? "history.aiDraft.generationInstructionLabel"
                              : "history.aiDraft.refinementLabel"
                          )}
                        </label>
                        <Textarea
                          id={`ai-draft-instruction-${draft.id}`}
                          value={generationInstruction}
                          disabled={actionsDisabled || isOutcomePending}
                          placeholder={t(
                            "history.aiDraft.refinementPlaceholder"
                          )}
                          className="max-h-40 min-h-20 resize-y"
                          onChange={(event) =>
                            setGenerationInstruction(event.target.value)
                          }
                        />
                        <div className="flex flex-wrap justify-between gap-x-2 gap-y-1 text-xs text-muted-foreground">
                          <span>{t("history.aiDraft.refinementHelp")}</span>
                          <span>
                            {t("history.aiDraft.guidanceCount", {
                              current: generationInstructionLength,
                              max: AI_DRAFT_GUIDANCE_MAX_LENGTH,
                            })}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={actionsDisabled || isOutcomePending}
                          onClick={() => {
                            setShowRefinement(false)
                            setGenerationInstruction("")
                          }}
                        >
                          {t("history.aiDraft.cancel")}
                        </Button>
                        <Button
                          size="sm"
                          disabled={
                            actionsDisabled ||
                            isOutcomePending ||
                            generationInstructionLength >
                              AI_DRAFT_GUIDANCE_MAX_LENGTH
                          }
                          onClick={() =>
                            handleGenerate({
                              instruction: generationInstruction,
                              ...(draft.result_type !== "human_handoff"
                                ? { refinement_source_draft_id: draft.id }
                                : {}),
                            })
                          }
                        >
                          {t("history.aiDraft.generateDraft")}
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              <Popover
                open={showDismissReasons}
                onOpenChange={(open) => {
                  setShowDismissReasons(open)
                  if (open) {
                    setShowRefinement(false)
                    setGenerationInstruction("")
                  }
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={actionsDisabled || isOutcomePending}
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                    {t("history.aiDraft.dismiss")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  sideOffset={8}
                  className="w-[min(22rem,calc(100vw-2rem))] space-y-3 p-3"
                  aria-label={t("history.aiDraft.dismissTitle")}
                >
                  <div className="space-y-0.5">
                    <div className="text-sm font-medium">
                      {t("history.aiDraft.dismissTitle")}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t("history.aiDraft.dismissReasonHelp")}
                    </div>
                  </div>
                  <Select
                    value={selectedDismissReason ?? "none"}
                    disabled={actionsDisabled || isOutcomePending}
                    onValueChange={(value) => {
                      const reason =
                        value === "none"
                          ? null
                          : (value as AIDraftDismissReason)
                      setSelectedDismissReason(reason)
                      if (reason !== "other") setDismissFeedback("")
                    }}
                  >
                    <SelectTrigger
                      size="sm"
                      className="w-full"
                      aria-label={t("history.aiDraft.dismissReasonLabel")}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent position="popper" align="end">
                      <SelectItem value="none">
                        {t("history.aiDraft.skipReason")}
                      </SelectItem>
                      {dismissReasons.map((reason) => (
                        <SelectItem key={reason} value={reason}>
                          {t(`history.aiDraft.dismissReasons.${reason}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedDismissReason === "other" && (
                    <div className="space-y-1.5">
                      <label
                        className="block text-xs font-medium"
                        htmlFor={`ai-draft-dismiss-feedback-${draft.id}`}
                      >
                        {t("history.aiDraft.otherFeedbackLabel")}
                      </label>
                      <Textarea
                        id={`ai-draft-dismiss-feedback-${draft.id}`}
                        value={dismissFeedback}
                        disabled={actionsDisabled || isOutcomePending}
                        placeholder={t(
                          "history.aiDraft.otherFeedbackPlaceholder"
                        )}
                        className="min-h-20 resize-y"
                        onChange={(event) =>
                          setDismissFeedback(event.target.value)
                        }
                      />
                      <div className="text-xs text-muted-foreground">
                        {t("history.aiDraft.guidanceCount", {
                          current: dismissFeedbackLength,
                          max: AI_DRAFT_GUIDANCE_MAX_LENGTH,
                        })}
                      </div>
                    </div>
                  )}
                  {dismissErrorCode && (
                    <div
                      className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-2 text-xs text-destructive"
                      role="alert"
                    >
                      <AlertCircle
                        className="mt-0.5 h-3.5 w-3.5 shrink-0"
                        aria-hidden="true"
                      />
                      <span>
                        {t(`history.aiDraft.errors.${dismissErrorCode}`, {
                          defaultValue: t("history.aiDraft.errors.generic"),
                        })}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={actionsDisabled || isOutcomePending}
                      onClick={() => {
                        resetDismissForm()
                      }}
                    >
                      {t("history.aiDraft.cancel")}
                    </Button>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={
                          actionsDisabled ||
                          isOutcomePending ||
                          dismissFeedbackLength > AI_DRAFT_GUIDANCE_MAX_LENGTH
                        }
                        onClick={() =>
                          void handleDismiss({
                            ...(selectedDismissReason
                              ? { reason: selectedDismissReason }
                              : {}),
                            feedback: dismissFeedback,
                          })
                        }
                      >
                        {t("history.aiDraft.dismissOnly")}
                      </Button>
                      <Button
                        size="sm"
                        disabled={
                          actionsDisabled ||
                          isOutcomePending ||
                          dismissFeedbackLength >
                            AI_DRAFT_GUIDANCE_MAX_LENGTH ||
                          (selectedDismissReason === "other" &&
                            !dismissFeedback.trim())
                        }
                        onClick={() =>
                          void handleDismiss({
                            ...(selectedDismissReason
                              ? { reason: selectedDismissReason }
                              : {}),
                            feedback: dismissFeedback,
                            regenerate: true,
                          })
                        }
                      >
                        {t("history.aiDraft.dismissAndRegenerate")}
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </footer>
          )}
        </section>
      )}

      {draft && !isGenerating && showConflict && (
        <div
          className="rounded-lg border border-border/80 bg-muted/25 px-3 py-3"
          role="group"
          aria-label={t("history.aiDraft.conflictTitle")}
        >
          <div className="text-sm font-medium">
            {t("history.aiDraft.conflictTitle")}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {t("history.aiDraft.conflictDescription")}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={actionsDisabled || isOutcomePending}
              onClick={() => void applyWithMode("replace")}
            >
              {t("history.aiDraft.replace")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={actionsDisabled || isOutcomePending}
              onClick={() => void applyWithMode("insert")}
            >
              {t("history.aiDraft.insertBelow")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={actionsDisabled || isOutcomePending}
              onClick={() => setShowConflict(false)}
            >
              {t("history.aiDraft.backToPreview")}
            </Button>
          </div>
        </div>
      )}

      {!draft && !isGenerating && !errorCode && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1">
          <div className="flex items-center gap-0.5">
            <Button
              size="sm"
              variant="ghost"
              className="h-8 px-2 text-muted-foreground hover:text-foreground"
              disabled={actionsDisabled}
              onClick={() => void handleGenerate()}
            >
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              {t("history.aiDraft.generate")}
            </Button>
            {showToolSupportInfo && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:text-foreground"
                    aria-label={t(
                      isToolSupportUnavailable
                        ? "history.aiDraft.unsupportedTools.unavailable.triggerLabel"
                        : "history.aiDraft.unsupportedTools.triggerLabel"
                    )}
                  >
                    <Info className="h-4 w-4" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  align="start"
                  sideOffset={8}
                  className="w-[min(20rem,calc(100vw-2rem))] space-y-2 p-3"
                >
                  {isToolSupportUnavailable ? (
                    <>
                      <div className="text-sm font-medium">
                        {t(
                          "history.aiDraft.unsupportedTools.unavailable.title"
                        )}
                      </div>
                      <p className="text-xs font-normal text-background/80">
                        {t(
                          "history.aiDraft.unsupportedTools.unavailable.description"
                        )}
                      </p>
                      <p className="border-t border-background/15 pt-2 text-xs font-normal text-background/80">
                        {t("history.aiDraft.unsupportedTools.unavailable.note")}
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="text-sm font-medium">
                        {t("history.aiDraft.unsupportedTools.title", {
                          count: unsupportedToolNames.length,
                        })}
                      </div>
                      <p className="text-xs font-normal text-background/80">
                        {t("history.aiDraft.unsupportedTools.description")}
                      </p>
                      <ul className="space-y-1.5 text-xs font-normal">
                        {unsupportedToolNames.map((name, index) => (
                          <li
                            key={`${unsupportedTools[index].id}:${name}`}
                            className="flex items-start gap-2"
                          >
                            <span
                              className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-current opacity-60"
                              aria-hidden="true"
                            />
                            <span className="break-words">{name}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="border-t border-background/15 pt-2 text-xs font-normal text-background/80">
                        {t("history.aiDraft.unsupportedTools.note")}
                      </p>
                    </>
                  )}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <span className="text-xs text-muted-foreground">
            {t("history.aiDraft.generateHelp")}
          </span>
        </div>
      )}
    </div>
  )
}

interface AIDraftDraftVersionProps {
  draftTarget: AIDraftTarget
  summary: AIDraftSummaryResponse
  active: boolean
}

export const formatAIDraftTimestamp = (value: string, locale?: string) =>
  new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...(locale?.toLowerCase().startsWith("zh")
      ? { dayPeriod: "short" as const }
      : {}),
  }).format(new Date(value))

interface AIDraftVersionNavigationContextValue {
  currentIndex: number
  total: number
  previous: () => void
  next: () => void
}

const AIDraftVersionNavigationContext =
  createContext<AIDraftVersionNavigationContextValue | null>(null)

const AIDraftVersionNavigationActions = () => {
  const { t } = useTranslation()
  const navigation = useContext(AIDraftVersionNavigationContext)
  if (!navigation || navigation.total <= 1) return null

  return (
    <nav
      className="ml-auto flex items-center gap-1 text-sm text-muted-foreground"
      aria-label={t("history.aiDraft.versionNavigation")}
    >
      <Button
        size="icon"
        variant="ghost"
        className="h-8 w-8"
        disabled={navigation.currentIndex === 0}
        aria-label={t("history.aiDraft.previousVersion")}
        onClick={navigation.previous}
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
      </Button>
      <span className="min-w-12 text-center font-medium" aria-live="polite">
        {t("history.aiDraft.versionCount", {
          current: navigation.currentIndex + 1,
          total: navigation.total,
        })}
      </span>
      <Button
        size="icon"
        variant="ghost"
        className="h-8 w-8"
        disabled={navigation.currentIndex === navigation.total - 1}
        aria-label={t("history.aiDraft.nextVersion")}
        onClick={navigation.next}
      >
        <ChevronRight className="h-4 w-4" aria-hidden="true" />
      </Button>
    </nav>
  )
}

const AIDraftActionBar = ({ detail }: { detail?: AIDraftResponse }) => {
  const { t } = useTranslation()
  const navigation = useContext(AIDraftVersionNavigationContext)
  if (!detail && (!navigation || navigation.total <= 1)) return null

  return (
    <div className="mt-2 flex w-full flex-wrap items-center justify-start gap-0 rounded-lg border-0">
      {detail && detail.result_type !== "human_handoff" && (
        <TipIconButton
          tip={t("history.aiDraft.copy")}
          button={
            <div className="rounded-sm transition duration-300 ease-in-out hover:bg-slate-100 dark:hover:bg-slate-800">
              <CopyButton
                content={detail.content}
                className="size-8 border-0 shadow-none"
                aria-label={t("history.aiDraft.copy")}
              />
            </div>
          }
        />
      )}
      {detail?.cost_credits != null && (
        <UsageMetaTooltip
          costCredits={detail.cost_credits}
          convId={detail.id}
          copilotField="history.aiDraft.usageMeta"
          align="start"
        />
      )}
      <AIDraftVersionNavigationActions />
    </div>
  )
}

const AIDraftDraftVersion = ({
  draftTarget,
  summary,
  active,
}: AIDraftDraftVersionProps) => {
  const { t, i18n } = useTranslation()
  const { data, fetchFn, status } = useGetAIDraftApi(draftTarget, summary.id)
  const detail = data?.data

  useEffect(() => {
    if (active && !detail && status === "idle")
      void fetchFn({ cache: "no-store" })
  }, [active, detail, fetchFn, status])

  const outcomeLabel = t(
    summary.result_type === "human_handoff" && summary.outcome === "generated"
      ? "history.aiDraft.previewCompleted"
      : `history.aiDraft.outcomes.${summary.outcome}`
  )
  const date = formatAIDraftTimestamp(
    summary.created_at,
    i18n.resolvedLanguage || i18n.language
  )
  const hasToolActivities = Boolean(detail?.tool_activities.length)

  return (
    <article
      className="relative mr-auto w-full rounded-xl bg-muted/30 px-3 py-3"
      data-message-source="ai-draft"
      data-testid={`ai-draft-timeline-${summary.id}`}
      hidden={!active}
    >
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full text-border/70"
        focusable="false"
      >
        <rect
          x="1"
          y="1"
          width="calc(100% - 2px)"
          height="calc(100% - 2px)"
          rx="11"
          fill="none"
          stroke="currentColor"
          strokeDasharray="10 6"
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <header
        className={cn(
          "flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground",
          hasToolActivities ? "mb-4" : "mb-6"
        )}
      >
        <Badge variant="secondary" className="h-5 px-2 text-xs font-medium">
          {t(
            summary.mode === "agent_preview"
              ? "history.aiDraft.agentPreviewTitle"
              : "history.aiDraft.timelineTitle"
          )}
        </Badge>
        <span aria-hidden="true">·</span>
        <span>
          {t("history.aiDraft.generatedBy", {
            name: summary.requested_by_name,
            date,
          })}
        </span>
        <span aria-hidden="true">·</span>
        <span>{outcomeLabel}</span>
      </header>
      <div>
        {detail ? (
          <>
            <AIDraftActivityCards activities={detail.tool_activities} />
            <AIDraftMarkdown
              content={detail.content}
              resultType={detail.result_type}
              actionBar={
                <>
                  {detail.dismiss_reason && (
                    <div className="mt-3 text-xs text-muted-foreground">
                      {t("history.aiDraft.dismissedBecause", {
                        reason: t(
                          `history.aiDraft.dismissReasons.${detail.dismiss_reason}`
                        ),
                      })}
                    </div>
                  )}
                  {detail.refinement_source_draft_id && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      {t("history.aiDraft.refinedFrom", {
                        id: detail.refinement_source_draft_id,
                      })}
                    </div>
                  )}
                  {detail.generation_instruction && (
                    <div className="mt-2 max-w-prose whitespace-pre-wrap text-xs text-muted-foreground">
                      {t("history.aiDraft.instructionDetail", {
                        instruction: detail.generation_instruction,
                      })}
                    </div>
                  )}
                  {detail.dismiss_feedback && (
                    <div className="mt-2 max-w-prose whitespace-pre-wrap text-xs text-muted-foreground">
                      {t("history.aiDraft.feedbackDetail", {
                        feedback: detail.dismiss_feedback,
                      })}
                    </div>
                  )}
                  <AIDraftFormProposalPreview
                    proposal={getAIDraftFormProposal(detail)}
                  />
                  <AIDraftActionBar detail={detail} />
                </>
              }
              className={hasToolActivities ? "mt-6" : undefined}
            />
          </>
        ) : status === "error" ? (
          <div className="flex items-center justify-between gap-3 text-xs text-destructive">
            <span>{t("history.aiDraft.errors.generic")}</span>
            <Button
              size="sm"
              variant="outline"
              className="h-7"
              onClick={() => void fetchFn({ cache: "no-store" })}
            >
              {t("history.aiDraft.retry")}
            </Button>
          </div>
        ) : (
          <div
            className="flex items-center gap-2 text-xs text-muted-foreground"
            role="status"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            {t("history.aiDraft.loadingRecord")}
          </div>
        )}
      </div>
      {!detail && <AIDraftActionBar />}
    </article>
  )
}

interface AIDraftVersionSwitcherProps {
  draftTarget: AIDraftTarget
  summaries: AIDraftSummaryResponse[]
  actualNode?: ReactNode
  className?: string
}

export const AIDraftVersionSwitcher = ({
  draftTarget,
  summaries,
  actualNode,
  className,
}: AIDraftVersionSwitcherProps) => {
  const targetKey = aiDraftTargetKey(draftTarget)
  const [currentIndex, setCurrentIndex] = useState(0)
  const actualOffset = actualNode == null ? 0 : 1
  const total = summaries.length + actualOffset
  const navigation = useMemo<AIDraftVersionNavigationContextValue>(
    () => ({
      currentIndex,
      total,
      previous: () => setCurrentIndex((index) => Math.max(0, index - 1)),
      next: () => setCurrentIndex((index) => Math.min(total - 1, index + 1)),
    }),
    [currentIndex, total]
  )

  useEffect(() => setCurrentIndex(0), [actualOffset, summaries, targetKey])

  if (total === 0) return null

  return (
    <AIDraftVersionNavigationContext.Provider value={navigation}>
      <section
        className={cn("my-3", className)}
        data-testid="ai-draft-version-switcher"
      >
        {actualNode != null && (
          <div hidden={currentIndex !== 0}>
            {actualNode}
            <AIDraftActionBar />
          </div>
        )}
        {summaries.map((summary, index) => (
          <AIDraftDraftVersion
            key={summary.id}
            draftTarget={draftTarget}
            summary={summary}
            active={currentIndex === index + actualOffset}
          />
        ))}
      </section>
    </AIDraftVersionNavigationContext.Provider>
  )
}
